const STORAGE_KEY = 'spacasnake_high_scores';
const SESSION_KEY = 'spacasnake_current_session';
const MAX_SCORES = 100;

// Recover any incomplete session on app load (runs once at module init)
const recoverSessionOnLoad = () => {
  try {
    const sessionStr = localStorage.getItem(SESSION_KEY);
    if (!sessionStr) return;

    const session = JSON.parse(sessionStr);
    localStorage.removeItem(SESSION_KEY);

    if (session.score < 5) return; // Don't save if score is less than initial

    const stored = localStorage.getItem(STORAGE_KEY);
    const scores = stored ? JSON.parse(stored) : [];

    scores.push({
      playerName: session.playerName,
      score: session.score,
      timestamp: session.startTime,
    });

    scores.sort((a: { score: number }, b: { score: number }) => b.score - a.score);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scores.slice(0, MAX_SCORES)));
  } catch {
    // Ignore errors
  }
};

// Run recovery immediately when this module is first imported
recoverSessionOnLoad();

export interface HighScoreEntry {
  playerName: string;
  score: number;
  timestamp: number;
}

interface CurrentSession {
  playerName: string;
  score: number;
  startTime: number;
}

export const useHighScore = () => {
  const getScores = (): HighScoreEntry[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const saveScoresToStorage = (scores: HighScoreEntry[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(scores));
    } catch {
      // localStorage full or unavailable
    }
  };

  // Start a new game session
  const startSession = (playerName: string) => {
    const session: CurrentSession = {
      playerName: playerName || 'Player',
      score: 0,
      startTime: Date.now(),
    };
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch {
      // localStorage unavailable
    }
  };

  // Update the current session's score (called periodically)
  const updateSession = (score: number) => {
    try {
      const sessionStr = localStorage.getItem(SESSION_KEY);
      if (sessionStr) {
        const session: CurrentSession = JSON.parse(sessionStr);
        session.score = score;
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      }
    } catch {
      // localStorage unavailable
    }
  };

  // End session and finalize score (called on death)
  const endSession = (): { isNewHighScore: boolean; rank: number } | null => {
    try {
      const sessionStr = localStorage.getItem(SESSION_KEY);
      if (!sessionStr) return null;

      const session: CurrentSession = JSON.parse(sessionStr);
      localStorage.removeItem(SESSION_KEY);

      if (session.score <= 0) return null;

      return saveScore(session.playerName, session.score);
    } catch {
      return null;
    }
  };

  // Save score to leaderboard
  const saveScore = (playerName: string, score: number): { isNewHighScore: boolean; rank: number } => {
    const scores = getScores();
    const newEntry: HighScoreEntry = {
      playerName: playerName || 'Player',
      score,
      timestamp: Date.now(),
    };

    scores.push(newEntry);
    scores.sort((a, b) => b.score - a.score);
    const trimmed = scores.slice(0, MAX_SCORES);

    saveScoresToStorage(trimmed);

    const rank = trimmed.findIndex(
      (s) => s.timestamp === newEntry.timestamp && s.score === newEntry.score
    ) + 1;
    const isNewHighScore = rank === 1;

    return { isNewHighScore, rank };
  };

  const getTopScores = (limit = 5): HighScoreEntry[] => {
    return getScores().slice(0, limit);
  };

  const getPersonalBest = (): number => {
    const scores = getScores();
    return scores.length > 0 ? scores[0].score : 0;
  };

  return {
    startSession,
    updateSession,
    endSession,
    saveScore,
    getTopScores,
    getPersonalBest
  };
};
